import React, { useState, useEffect } from 'react';
import { globalShortcutsService } from '../services/globalShortcutsService';
import GlobalQuickTaskModal from '../components/GlobalQuickTaskModal/GlobalQuickTaskModal';

/**
 * Hook to enable global task creation shortcut (Cmd+Shift+T)
 * 
 * Usage in App.tsx:
 * ```tsx
 * const { GlobalQuickTaskModal } = useGlobalTaskShortcut();
 * 
 * return (
 *   <>
 *     <YourApp />
 *     {GlobalQuickTaskModal}
 *   </>
 * );
 * ```
 */
export const useGlobalTaskShortcut = () => {
  const [isQuickTaskOpen, setIsQuickTaskOpen] = useState(false);

  useEffect(() => {
    console.log('🎯 [useGlobalTaskShortcut] Hook mounted, registering Cmd+Shift+T...');
    
    // Register Cmd+Shift+T shortcut
    globalShortcutsService.register({
      id: 'global_quick_task',
      key: 't',
      modifiers: {
        cmd: true,
        shift: true
      },
      description: 'Quick add task',
      handler: () => {
        console.log('🚀 [useGlobalTaskShortcut] Handler called! Opening quick task modal...');
        setIsQuickTaskOpen(true);
      }
    });

    console.log('✅ [useGlobalTaskShortcut] Shortcut registered successfully');

    // Cleanup on unmount
    return () => {
      console.log('🧹 [useGlobalTaskShortcut] Hook unmounting, unregistering...');
      globalShortcutsService.unregister('global_quick_task');
    };
  }, []);

  // Log modal state changes
  useEffect(() => {
    console.log('📊 [useGlobalTaskShortcut] Modal state changed:', {
      isOpen: isQuickTaskOpen
    });
  }, [isQuickTaskOpen]);

  // Render modal directly (no lazy loading for instant speed)
  const GlobalQuickTaskModalComponent = isQuickTaskOpen ? (
    <GlobalQuickTaskModal
      isOpen={isQuickTaskOpen}
      onClose={() => {
        console.log('🚪 [useGlobalTaskShortcut] Closing modal...');
        setIsQuickTaskOpen(false);
      }}
    />
  ) : null;

  return {
    GlobalQuickTaskModal: GlobalQuickTaskModalComponent,
    openQuickTask: () => setIsQuickTaskOpen(true),
    closeQuickTask: () => setIsQuickTaskOpen(false),
    isQuickTaskOpen
  };
};

